import ApiClient from '@/util/ApiClient'
import { defineStore } from 'pinia'
import SpaceVariable from '../models/SpaceVariable'
import { getEffectiveVariableValue, getVariableValueFlags } from '@/util/variable-state'
import CreateSpaceVariableRequest from '../models/CreateSpaceVariableRequest'
import CreateVariableAssignmentRequest, { VariableAssignment } from '../models/CreateVariableAssignmentRequest'
import UpdateVariableValueRequest from '../models/UpdateVariableValueRequest'
import DropdownOption from '../models/DropdownOption'

export type SpaceVariableState = {
  spaceVariables: Map<string, SpaceVariable[]>
  /**
   * Bumped by changes made inside the Map, which Vue 2 cannot see. Only static rendering reads it; the Map itself is
   * left in place there, so watchers on its reference do not start running on those changes.
   */
  revision: number
}

export const useSpaceVariableStore = defineStore('spaceVariable', {
  state: (): SpaceVariableState => ({
    spaceVariables: new Map(),
    revision: 0
  }),
  actions: {
    /** `rethrow` is for the PDF export, which must know the variables it shows failed to load. */
    async fetchVariables(spaceId: string, { rethrow = false }: { rethrow?: boolean } = {}): Promise<SpaceVariable[]> {
      try {
        const response = await ApiClient.get<SpaceVariable[]>(`/spaces/${spaceId}/variables`)
        this.setVariables(spaceId, response.data)
        return response.data
      } catch (error) {
        // Log error but don't throw to prevent blocking space load
        console.error(`Failed to fetch variables for space ${spaceId}:`, error)
        if (rethrow) throw error
        return []
      }
    },
    async fetchTemplateVariables(templateId: string): Promise<SpaceVariable[]> {
      try {
        const response = await ApiClient.get<SpaceVariable[]>(`/space-page-templates/${templateId}/variables`)
        this.setVariables(templateId, response.data)
        return response.data
      } catch (error) {
        console.error(`Failed to fetch variables for template ${templateId}:`, error)
        return []
      }
    },
    async createTemplateVariable(templateId: string, request: CreateSpaceVariableRequest): Promise<SpaceVariable> {
      const response = await ApiClient.post<SpaceVariable>(`/space-page-templates/${templateId}/variables`, request)
      const createdVariable = response.data

      const existingVariables = this.spaceVariables.get(templateId)
      if (existingVariables) {
        existingVariables.push(createdVariable)
      } else {
        this.spaceVariables.set(templateId, [createdVariable])
      }
      this.revision++

      return createdVariable
    },
    async updateTemplateVariable(
      templateId: string,
      variableId: string,
      request: {
        variableName: string
        displayLabel?: string
        variableType: string
        defaultValue?: string
        dateFormat?: string
      }
    ): Promise<SpaceVariable> {
      const response = await ApiClient.put<SpaceVariable>(
        `/space-page-templates/${templateId}/variables/${variableId}`,
        request
      )
      const updatedVariable = response.data

      // Update store with response data (replace existing variable in array)
      const existingVariables = this.spaceVariables.get(templateId)
      if (existingVariables) {
        const index = existingVariables.findIndex(v => v.id === variableId)
        if (index !== -1) {
          existingVariables[index] = updatedVariable
        } else {
          existingVariables.push(updatedVariable)
        }
        // Trigger reactivity by creating new Map
        const newMap = new Map(this.spaceVariables)
        newMap.set(templateId, existingVariables)
        this.spaceVariables = newMap
      } else {
        this.spaceVariables.set(templateId, [updatedVariable])
        this.revision++
      }

      return updatedVariable
    },
    async createVariable(spaceId: string, request: CreateSpaceVariableRequest): Promise<SpaceVariable> {
      const response = await ApiClient.post<SpaceVariable>(`/spaces/${spaceId}/variables`, request)
      const createdVariable = response.data

      // Optimize Map operations - get once, check if exists, then set
      const existingVariables = this.spaceVariables.get(spaceId)
      if (existingVariables) {
        // Mutate existing array for better performance (Vue reactivity handles it)
        existingVariables.push(createdVariable)
      } else {
        // Create new array only if needed
        this.spaceVariables.set(spaceId, [createdVariable])
      }
      this.revision++

      return createdVariable
    },
    getVariablesBySpaceId(spaceId: string): SpaceVariable[] {
      // Return empty array directly instead of using || operator for better performance
      const variables = this.spaceVariables.get(spaceId)
      return variables || []
    },
    getVariableByName(spaceId: string, variableName: string): SpaceVariable | undefined {
      const variables = this.getVariablesBySpaceId(spaceId)
      return variables.find(v => v.variableName.toLowerCase() === variableName.toLowerCase())
    },
    getVariableById(spaceId: string, variableId: string): SpaceVariable | undefined {
      const variables = this.getVariablesBySpaceId(spaceId)
      return variables.find(v => v.id === variableId)
    },
    getVariableValue(variable: SpaceVariable): string | null {
      return getEffectiveVariableValue(variable)
    },
    hasVariableValue(variable: SpaceVariable): boolean {
      const { hasDefaultValue, hasRecipientValue } = getVariableValueFlags(variable)
      return hasDefaultValue || hasRecipientValue
    },
    getUnfilledVariablesForUser(spaceId: string, userId: string): SpaceVariable[] {
      const variables = this.getVariablesBySpaceId(spaceId)
      return variables.filter(v => {
        const hasRecipient = v.assignedToRecipientId === userId
        const hasNoDefault = !v.defaultValue || !v.defaultValue.trim()
        const hasNoRecipientValue = !v.recipientValue || !v.recipientValue.trim()
        return hasRecipient && hasNoDefault && hasNoRecipientValue
      })
    },
    getVariablesForUser(spaceId: string, userId: string): SpaceVariable[] {
      const variables = this.getVariablesBySpaceId(spaceId)
      return variables.filter(v => v.assignedToRecipientId === userId)
    },
    getFilledVariablesForUser(spaceId: string, userId: string): SpaceVariable[] {
      const variables = this.getVariablesForUser(spaceId, userId)
      return variables.filter(v => {
        const recipientValue = v.recipientValue?.trim()
        return !!recipientValue
      })
    },
    async saveAssignments(spaceId: string, request: CreateVariableAssignmentRequest): Promise<void> {
      await ApiClient.post(`/spaces/${spaceId}/variable-assignments`, request)
      // Refresh variables to get updated assignment data
      await this.fetchVariables(spaceId)
    },
    async updateAssignment(spaceId: string, variableId: string, recipientId: string): Promise<void> {
      const assignment = new VariableAssignment()
      assignment.spaceId = spaceId
      assignment.variableId = variableId
      assignment.recipientId = recipientId

      await ApiClient.put(`/spaces/${spaceId}/variable-assignments`, assignment)
      // Refresh variables to get updated assignment data
      await this.fetchVariables(spaceId)
    },
    async deleteAssignment(spaceId: string, variableId: string, recipientId: string): Promise<void> {
      const assignment = new VariableAssignment()
      assignment.spaceId = spaceId
      assignment.variableId = variableId
      assignment.recipientId = recipientId

      await ApiClient.delete(`/spaces/${spaceId}/variable-assignments`, { data: assignment })
      // Refresh variables to get updated assignment data
      await this.fetchVariables(spaceId)
    },
    async saveVariableValue(spaceId: string, request: UpdateVariableValueRequest): Promise<void> {
      await ApiClient.post(`/spaces/${spaceId}/variable-values`, request)
      // Refresh variables to get updated values
      await this.fetchVariables(spaceId)
    },
    async saveVariableValuesBatch(spaceId: string, requests: UpdateVariableValueRequest[]): Promise<void> {
      await ApiClient.post(`/spaces/${spaceId}/variable-values`, requests)
      // Refresh variables to get updated values
      await this.fetchVariables(spaceId)
    },
    async updateVariable(
      spaceId: string,
      variableId: string,
      request: {
        variableName: string
        displayLabel?: string
        variableType: string
        defaultValue?: string
        dateFormat?: string
        options?: DropdownOption[]
      }
    ): Promise<SpaceVariable> {
      const response = await ApiClient.put<SpaceVariable>(`/spaces/${spaceId}/variables/${variableId}`, request)
      const updatedVariable = response.data

      // Update store with response data (replace existing variable in array)
      const existingVariables = this.spaceVariables.get(spaceId)
      if (existingVariables) {
        const index = existingVariables.findIndex(v => v.id === variableId)
        if (index !== -1) {
          existingVariables[index] = updatedVariable
        } else {
          existingVariables.push(updatedVariable)
        }
        // Trigger reactivity by creating new Map
        const newMap = new Map(this.spaceVariables)
        newMap.set(spaceId, existingVariables)
        this.spaceVariables = newMap
      } else {
        this.spaceVariables.set(spaceId, [updatedVariable])
        this.revision++
      }

      return updatedVariable
    },
    async deleteVariable(spaceId: string, variableId: string): Promise<void> {
      await ApiClient.delete(`/spaces/${spaceId}/variables/${variableId}`)

      // On successful response: Remove variable from store
      const existingVariables = this.spaceVariables.get(spaceId)
      if (existingVariables) {
        const filtered = existingVariables.filter(v => v.id !== variableId)
        // Update Map to trigger reactivity
        const newMap = new Map(this.spaceVariables)
        newMap.set(spaceId, filtered)
        this.spaceVariables = newMap
      }
    },
    async deleteTemplateVariable(templateId: string, variableId: string): Promise<void> {
      await ApiClient.delete(`/space-page-templates/${templateId}/variables/${variableId}`)

      const existingVariables = this.spaceVariables.get(templateId)
      if (existingVariables) {
        const filtered = existingVariables.filter(v => v.id !== variableId)
        const newMap = new Map(this.spaceVariables)
        newMap.set(templateId, filtered)
        this.spaceVariables = newMap
      }
    },
    setVariables(spaceId: string, variables: SpaceVariable[]): void {
      // Create a new Map to ensure reactivity is triggered
      const newMap = new Map(this.spaceVariables)
      newMap.set(spaceId, variables)
      this.spaceVariables = newMap
    },
    clearVariables(spaceId: string): void {
      this.spaceVariables.delete(spaceId)
      this.revision++
    }
  }
})

export type SpaceVariableStore = ReturnType<typeof useSpaceVariableStore>
